import * as React from 'react';

import { Button, Grid, Typography2 } from '@coursera/cds-core';
import { EditIcon, TrashIcon } from '@coursera/cds-icons';

import { useTableMultiSelect } from 'bundles/admin-shared/components/TableMultiSelect';

import _t from 'i18n!nls/staff-management';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { staffTableRowKey } from '../../StaffTable';
import type { StaffTableRow } from '../../types';

interface Props {
  rowsFromAllPages: StaffTableRow[];
  onBatchDelete: (rows: StaffTableRow[]) => void;
  onBatchEdit: (rows: StaffTableRow[]) => void;
}

interface RenderComponentProps {
  nothingIsSelected: boolean;
  staffCount: number;
  onBatchDelete: () => void;
  onBatchEdit: () => void;
}

const pickStaffTableRows = (rows: StaffTableRow[], ids: Set<string>): StaffTableRow[] =>
  rows.filter((row) => ids.has(staffTableRowKey(row)));

export const TableBulkActionsBarRenderComponent = ({
  onBatchDelete,
  onBatchEdit,
  nothingIsSelected,
  staffCount,
}: RenderComponentProps) => {
  return (
    <Grid container justifyContent="space-between" alignItems="center" xs={12} spacing={32}>
      <Grid item>
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          {_t('Showing #{numStaff} staff', {
            numStaff: staffCount,
          })}
        </Typography2>
      </Grid>
      <Grid item>
        <Grid container alignItems="center">
          <Grid item>
            <Button
              variant="ghost"
              size="small"
              icon={<EditIcon />}
              iconPosition="before"
              onClick={onBatchEdit}
              disabled={nothingIsSelected}
            >
              {_t('Edit role')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="ghost"
              size="small"
              icon={<TrashIcon />}
              iconPosition="before"
              onClick={onBatchDelete}
              disabled={nothingIsSelected}
            >
              {_t('Remove')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const TableBulkActionsBar = ({ rowsFromAllPages, onBatchDelete, onBatchEdit }: Props) => {
  const { selectionState, selection } = useTableMultiSelect();

  const nothingIsSelected = selectionState === 'NONE';
  const staffCount = rowsFromAllPages.length;

  return (
    <TableBulkActionsBarRenderComponent
      nothingIsSelected={nothingIsSelected}
      staffCount={staffCount}
      onBatchDelete={() => {
        onBatchDelete(pickStaffTableRows(rowsFromAllPages, selection));
      }}
      onBatchEdit={() => onBatchEdit(pickStaffTableRows(rowsFromAllPages, selection))}
    />
  );
};

export default TableBulkActionsBar;
